<script lang="ts" setup>
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import BaseCustomTooltip from '@/common/components/molecules/baseCustomTooltip/BaseCustomTooltip.vue';
import { Props, Emit, setup } from './modernGridColumnSetting.setup';

const props = withDefaults(defineProps<Props>(), {
  columnSettingList: () => [],
});
const emit = defineEmits<Emit>();

const { tooltipRef, t, columnSettings, checkColumnGroup, searchWord, onCheckColumn, onSave } =
  setup(props, emit);

defineExpose({
  show: (e: MouseEvent) => tooltipRef.value?.show(e),
  hide: () => tooltipRef.value?.hide(),
});
</script>

<template>
  <base-custom-tooltip
    ref="tooltipRef"
    :use-deselect="false"
    custom-class="modern-grid__column-setting"
  >
    <slot name="tooltip-content">
      <div class="modern-grid__column-setting__container">
        <div class="modern-grid__column-setting__header">
          <p class="modern-grid__column-setting__header__title">{{ t('WORD.COLUMN_LIST') }}</p>
        </div>
        <div class="modern-grid__column-setting__body">
          <div class="modern-grid__column-setting__body__search-field">
            <ev-text-field
              v-model="searchWord"
              :placeholder="t('WORD.SEARCH')"
              :max-length="50"
            >
              <template #icon-prefix>
                <ev-icon icon="ev-icon-search2" />
              </template>
            </ev-text-field>
          </div>
          <div class="modern-grid__column-setting__body__list-wrapper">
            <ev-checkbox-group
              v-if="columnSettings?.length"
              v-model="checkColumnGroup"
              @change="onCheckColumn"
            >
              <ev-checkbox
                v-for="(column, idx) in columnSettings"
                :key="`column_${idx}`"
                :label="column?.text"
                :disabled="!!column?.fixed"
                :tooltip-title="column?.label ?? ''"
              >
                {{ column?.label }}
              </ev-checkbox>
            </ev-checkbox-group>
            <div
              v-else
              class="modern-grid__column-setting__body__empty"
            >
              {{ t('MESSAGE.NO_DATA') }}
            </div>
          </div>
        </div>
        <div class="modern-grid__column-setting__footer">
          <base-button
            type="primary"
            @click="onSave"
          >
            {{ t('WORD.OK') }}
          </base-button>
        </div>
      </div>
    </slot>
  </base-custom-tooltip>
</template>

<style lang="scss" scoped>
.modern-grid__column-setting {
  &__container {
    --header-height: 36px;
    --footer-btn-size: 24px;
    --inner-padding: 12px;

    display: flex;
    flex-direction: column;
    width: 240px !important;
    border: none !important;
    background-color: var(--grid-column-setting-bg-color) !important;
    box-shadow: var(--window-shadow);

    @include set-scrollbar();
  }
  &__header {
    height: var(--header-height);
    padding: 0 10px;
    background-color: var(--grid-column-setting-header-bg-color);
    &__title {
      font-size: 15px;
      font-weight: 500;
      line-height: var(--header-height);
      color: var(--main-font-color);
    }
  }
  &__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    height: calc(100% - var(--header-height) - var(--footer-btn-size) - var(--inner-padding));
    padding: var(--inner-padding);
    &__search-field {
      flex: 0;
      .ev-text-field {
        width: 100% !important;
        margin-top: 0 !important;
      }
      .ev-input {
        height: 24px !important;
      }
    }
    &__list-wrapper {
      display: flex;
      align-items: start;
      justify-content: center;
      height: 168px !important;
      overflow: auto;
      border: 1px solid var(--grid-column-setting-content-border-color);
      border-top: 1px solid #ced4da;
      border-bottom: 1px solid #ced4da;
      border-radius: 2px;
      background-color: var(--grid-column-setting-content-bg-color);
    }
    .ev-checkbox-group {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    .ev-checkbox {
      padding: 6px 8px !important;
      &:hover {
        background-color: var(--grid-column-setting-checkbox-hover-bg-color);
      }
    }
    &__empty {
      width: 100%;
      padding: var(--inner-padding);
      padding-top: 15px;
      font-size: 12px;
      color: var(--empty-color);
      text-align: center;
    }
  }
  &__footer {
    display: flex;
    padding: 0 var(--inner-padding) var(--inner-padding) !important;

    .ev-button {
      height: var(--footer-btn-size) !important;
      margin-left: auto;
      font-size: 12px;
    }
  }
}
</style>
